// SignalPath API client.
//
// Single source of truth for talking to the model. The prototype had two copies
// of this function — one here and one inline in SignalPath.jsx — which had
// already begun to drift. There is now exactly one.
//
// The client sends a prompt and nothing else. Model, max_tokens, and the system
// prompt are fixed server-side in the Worker so a hostile caller cannot run up
// the bill by asking for an expensive model or a huge completion.

import { API_ENDPOINT } from "./constants";
import { auth } from "../lib/firebase";

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/**
 * Ask the model for a JSON object.
 * @param {string} prompt
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<object>} parsed JSON
 * @throws {ApiError} on transport, rate-limit, or parse failure — callers are
 *         expected to catch and fall back to pre-computed data (ADR-002).
 */
export async function callClaude(prompt, opts = {}) {
  // Live analysis requires a signed-in user (ADR-023). Anonymous callers get a
  // 401, which the caller treats like any other failure and falls back to
  // pre-computed analysis — a complete answer, just not a live one.
  const headers = { "Content-Type": "application/json" };
  const token = await auth?.currentUser?.getIdToken?.().catch(() => null);
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(API_ENDPOINT, {
    method: "POST",
    headers,
    body: JSON.stringify({ prompt }),
    signal: opts.signal,
  });

  if (res.status === 401) {
    throw new ApiError("Sign in for live analysis — showing research-backed analysis", 401);
  }
  if (res.status === 429) {
    throw new ApiError("Rate limited — showing research-backed analysis instead", 429);
  }
  if (!res.ok) {
    throw new ApiError(`Proxy returned ${res.status}`, res.status);
  }

  const data = await res.json();
  if (data.error) throw new ApiError(data.error.message || "Upstream error", 502);

  const text = data.content?.find((b) => b.type === "text")?.text || "";
  const match = text.match(/\{[\s\S]*\}/);
  if (!match) throw new ApiError("No JSON object found in model response", 502);

  try {
    return JSON.parse(match[0]);
  } catch {
    throw new ApiError("Model returned malformed JSON", 502);
  }
}

/** Run a promise alongside a minimum delay, so the loading animation can breathe. */
export function withMinDelay(promise, ms = 1200) {
  return Promise.all([promise, new Promise((r) => setTimeout(r, ms))]).then(([result]) => result);
}
